import sharedSupabase from '../config/supabase';
import {
  DELIVERY as DELIVERY_MODE,
  PICKUP as PICKUP_MODE,
  resolveBuyingMode,
  needsBuyingModeAnswer as needsBuyingModeAnswerPure,
} from '../utils/buyingMode';

/**
 * Vendor buying mode — the WRITE side.
 *
 * How a vendor is bought from, and whether anyone ever said, is decided by
 * `utils/buyingMode` — pure, no client, one definition. This module is what
 * talks to the database, plus the re-exports that keep existing importers
 * working.
 *
 * `vendors.fulfillment_type` decides what an order for that vendor IS: a
 * purchase order emailed to them ('delivery'), or a shopping list somebody
 * takes to the shop ('pickup'). It is NOT NULL DEFAULT 'delivery', so on its
 * own it cannot tell an answer from a default — every vendor that has never
 * been asked looks exactly like one that was asked and said "delivery".
 *
 * `fulfillment_type_source` is that missing bit:
 *   'default' — nobody has been asked. Ordering screens show a generic
 *               "Order" button and put the question up once, at first order.
 *   'user'    — a person chose (vendor editor, or the first-order question).
 *   'import'  — inferred from evidence by an import path; counts as answered.
 *   'history' — inferred from this store's own unambiguous order history
 *               (migration 20260814130000); counts as answered.
 *
 * A default is not an answer. Everything here exists to keep that true.
 *
 * Every function takes an optional trailing `client` (defaults to the shared
 * supabase singleton) so tests can inject a fake.
 */

export const DELIVERY = DELIVERY_MODE;
export const PICKUP = PICKUP_MODE;

export {
  resolveBuyingMode,
  buyingModePill,
  buyingModeSourceText,
  buyingModeExplanation,
  isAnswerableVendor,
  ANSWERED_SOURCES,
  BUYING_MODE_ANSWERS,
  DELIVERY_ANSWER_LABEL,
  PICKUP_ANSWER_LABEL,
} from '../utils/buyingMode';

/**
 * The shape every reader gets, whatever the row looked like. Kept for the
 * callers that predate `resolveBuyingMode`; `fulfillmentType` here is the
 * STORED type (the column default included), which is why it must never be
 * displayed as an answer on its own — read `.answered` first.
 */
export function normalizeFulfillment(row) {
  const { storedType, source, answered } = resolveBuyingMode(row);
  return { fulfillmentType: storedType, source, answered };
}

/**
 * True when this vendor still needs the question — the ONE definition, from
 * utils/buyingMode. A second, subtly different one is how a vendor gets asked
 * twice. Re-exported (not re-implemented) so the identity holds.
 *
 * A legacy vendor (a synthesised row whose id is a slug, not a UUID) can never
 * be written to, so it is never asked.
 */
export const needsBuyingModeAnswer = needsBuyingModeAnswerPure;

/**
 * Record the answer. Throws on failure, deliberately: the caller must not
 * create the order until this has stuck. An order created after a failed write
 * would ask the same question again on the next order, which is exactly the
 * nagging this feature exists to avoid.
 */
export async function setVendorFulfillment(vendorId, mode, client = sharedSupabase) {
  if (!vendorId) throw new Error('vendorId is required');
  if (mode !== DELIVERY && mode !== PICKUP) {
    throw new Error(`Unknown buying mode "${String(mode)}"`);
  }

  let actorId = null;
  try {
    const { data } = await client.auth.getUser();
    actorId = data?.user?.id ?? null;
  } catch (_) {
    actorId = null; // an unattributable answer is still an answer
  }

  const { error } = await client
    .from('vendors')
    .update({
      fulfillment_type: mode,
      fulfillment_type_source: 'user',
      fulfillment_type_set_at: new Date().toISOString(),
      fulfillment_type_set_by: actorId,
    })
    .eq('id', vendorId);
  if (error) throw new Error(error.message);
  return { vendorId: String(vendorId), fulfillmentType: mode, source: 'user', answered: true };
}
